import apiClient from './apiClient';

const API_BASE_URL = '/api/v1.0/rum/apps';

/**
 * App Management API Service
 * Handles all API calls related to RUM application management
 */
class AppManagementApi {
  /**
   * Create a new application
   * @param {Object} appData - Application data (name, description, domains, environment)
   * @returns {Promise<Object>} Created application with appId
   */
  async createApp(appData) {
    try {
      const response = await apiClient.post(API_BASE_URL, appData);
      // Backend returns ApiResponse<RumApplication> with { success, message, data, timestamp }
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to create app:', error);
      throw error;
    }
  }

  /**
   * List all applications
   * @param {Object} params - Query parameters (page, limit, status, etc.)
   * @returns {Promise<Array>} List of applications
   */
  async listApps(params = {}) {
    try {
      const response = await apiClient.get(API_BASE_URL, { params });
      // Backend returns ApiResponse<List<RumApplication>>
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to list apps:', error);
      throw error;
    }
  }

  /**
   * Get application details by ID
   * @param {string} appId - Application ID
   * @returns {Promise<Object>} Application details
   */
  async getApp(appId) {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/${appId}`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to get app:', error);
      throw error;
    }
  }

  /**
   * Update application details
   * @param {string} appId - Application ID
   * @param {Object} appData - Updated application data
   * @returns {Promise<Object>} Updated application
   */
  async updateApp(appId, appData) {
    try {
      const response = await apiClient.put(`${API_BASE_URL}/${appId}`, appData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to update app:', error);
      throw error;
    }
  }

  /**
   * Delete an application
   * @param {string} appId - Application ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteApp(appId) {
    try {
      await apiClient.delete(`${API_BASE_URL}/${appId}`);
      return true;
    } catch (error) {
      console.error('Failed to delete app:', error);
      throw error;
    }
  }

  /**
   * Generate a new API key for an application
   * @param {string} appId - Application ID
   * @param {Object} keyData - Key data (name, description, permissions)
   * @returns {Promise<Object>} Generated API key
   */
  async generateApiKey(appId, keyData = {}) {
    try {
      const response = await apiClient.post(`${API_BASE_URL}/${appId}/keys`, keyData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to generate API key:', error);
      throw error;
    }
  }

  /**
   * Revoke an API key
   * @param {string} appId - Application ID
   * @param {string} keyId - API key ID
   * @returns {Promise<boolean>} Success status
   */
  async revokeApiKey(appId, keyId) {
    try {
      await apiClient.delete(`${API_BASE_URL}/${appId}/keys/${keyId}`);
      return true;
    } catch (error) {
      console.error('Failed to revoke API key:', error);
      throw error;
    }
  }

  /**
   * Rotate an API key — issues a new key and marks the old one revoked_at = now()+24h
   * so deployed clients keep working during the grace window.
   */
  async rotateApiKey(appId, oldKeyId, keyData = {}) {
    try {
      const response = await apiClient.post(
        `${API_BASE_URL}/${appId}/keys/${oldKeyId}/rotate`, keyData);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to rotate API key:', error);
      throw error;
    }
  }

  /**
   * List all API keys for an application
   * @param {string} appId - Application ID
   * @returns {Promise<Array>} List of API keys
   */
  async listApiKeys(appId) {
    try {
      const response = await apiClient.get(`${API_BASE_URL}/${appId}/keys`);
      return response.data.data || response.data;
    } catch (error) {
      console.error('Failed to list API keys:', error);
      throw error;
    }
  }

  /**
   * Get SDK integration code for an application.
   *
   * The snippet always embeds the real API key (one-key-per-app model, the
   * key is retrievable from the platform). CDN URL, beacon endpoint, and SRI
   * hash come from the operator-managed RUM platform settings — falls back
   * to same-origin defaults if no override is configured.
   *
   * Call {@link #refreshPlatformSettings} on app boot so the cached values
   * are warm; this synchronous getter relies on that cache.
   *
   * @param {string} appId - Application ID
   * @param {string} apiKey - REAL API key (no placeholder substitution)
   * @param {string} environment - 'production' | 'staging' | 'development'
   * @param {object} [opts]
   * @param {string}  [opts.framework='vanilla'] - 'vanilla' | 'react' | 'next'
   * @returns {string} SDK integration code snippet
   */
  getSDKIntegrationCode(appId, apiKey, environment = 'production', opts = {}) {
    const framework = opts.framework || 'vanilla';
    const keyToken = apiKey || 'MISSING_KEY';
    const sdkUrl = this.getSDKCdnUrl();
    const beaconUrl = this.getBeaconEndpoint();
    const sriHash = this.getSDKSriHash();
    const integrity = sriHash ? ` integrity="${sriHash}" crossorigin="anonymous"` : '';

    if (framework === 'react') {
      return `// pages/_app.jsx (or src/main.jsx) — load once on app mount
import { useEffect } from 'react';

export default function App({ Component, pageProps }) {
  useEffect(() => {
    if (window.VeropsRUM) return;
    const s = document.createElement('script');
    s.src = '${sdkUrl}';
    ${sriHash ? `s.integrity = '${sriHash}'; s.crossOrigin = 'anonymous';` : ''}
    s.async = true;
    s.onload = () => window.VeropsRUM.init({
      appId: '${appId}',
      apiKey: '${keyToken}',
      endpoint: '${beaconUrl}',
      environment: '${environment}',
      respectDoNotTrack: true,
    });
    document.head.appendChild(s);
  }, []);
  return <Component {...pageProps} />;
}`;
    }

    if (framework === 'next') {
      return `// app/layout.jsx — Next.js 13+ App Router
import Script from 'next/script';

export default function RootLayout({ children }) {
  return (
    <html>
      <body>
        {children}
        <Script
          src="${sdkUrl}"
          ${sriHash ? `integrity="${sriHash}" crossOrigin="anonymous"` : ''}
          strategy="afterInteractive"
          onLoad={() => window.VeropsRUM.init({
            appId: '${appId}',
            apiKey: '${keyToken}',
            endpoint: '${beaconUrl}',
            environment: '${environment}',
            respectDoNotTrack: true,
          })}
        />
      </body>
    </html>
  );
}`;
    }

    // Default: vanilla HTML — paste before </head>
    return `<!-- VerOps RUM SDK v2 -->
<script src="${sdkUrl}"${integrity} defer></script>
<script>
  document.addEventListener('DOMContentLoaded', function () {
    window.VeropsRUM && window.VeropsRUM.init({
      appId: '${appId}',
      apiKey: '${keyToken}',
      endpoint: '${beaconUrl}',
      environment: '${environment}',
      respectDoNotTrack: true,
      // Privacy defaults — opt in to capture more if needed:
      //   trackClicks: true,
      //   trackForms: true,        // metadata only, never field values
      //   trackNetwork: true,      // metadata only, never bodies/headers
      //   allowedQueryParams: ['utm_source','utm_medium','utm_campaign']
    });
  });
</script>
<!--
  Consent gate (recommended):
    On user accept:  window.VeropsRUM.consent.grant();
    On user deny:    window.VeropsRUM.consent.deny();
  Default state is 'unknown' which lets events through; only an explicit
  deny() drops events. The consent API is callable BEFORE init() runs,
  so wire your consent banner straight to it.
-->`;
  }

  /**
   * Operator-configured CDN URL for the SDK file. Falls back to the
   * same-origin default when no override is set in platform settings.
   * Reads from the cache populated by {@link #refreshPlatformSettings}.
   */
  getSDKCdnUrl() {
    const cfg = (this._platformSettings || {});
    return cfg.sdkCdnUrl || `${window.location.origin}/cdn/rum-sdk/v2/verops-rum.min.js`;
  }

  /**
   * Operator-configured beacon ingest endpoint. Falls back to same-origin
   * default. The Apache vhost is expected to proxy the path to the Rust
   * gateway, but operators may point this at a different gateway URL.
   */
  getBeaconEndpoint() {
    const cfg = (this._platformSettings || {});
    return cfg.beaconEndpoint || `${window.location.origin}/rum/v2/beacon`;
  }

  /**
   * SRI integrity hash for the published SDK bundle.
   * Stored in platform settings — operator updates whenever SDK is republished.
   */
  getSDKSriHash() {
    const cfg = (this._platformSettings || {});
    return cfg.sdkSriHash || '';
  }

  /**
   * Fetch the current RUM platform settings (operator-tunable CDN URL,
   * beacon endpoint, SRI hash). Cached on the singleton so the synchronous
   * snippet getters above can read it without forcing every UI render to
   * await an API call.
   *
   * Call this on app boot AND when the operator saves new settings.
   */
  async refreshPlatformSettings() {
    try {
      const response = await apiClient.get('/api/v1.0/rum/platform-settings');
      this._platformSettings = response.data?.data || {};
    } catch (error) {
      console.warn('[RUM] Falling back to default platform settings:', error?.message);
      this._platformSettings = {};
    }
    return this._platformSettings;
  }

  /** Update RUM platform settings (admin only). */
  async updatePlatformSettings(payload) {
    const response = await apiClient.put('/api/v1.0/rum/platform-settings', payload);
    this._platformSettings = response.data?.data || {};
    return this._platformSettings;
  }

  /**
   * Recompute the SDK SRI hash by fetching the file from the configured
   * CDN URL server-side, hashing it, and persisting the result.
   *
   * <p>v2 response shape adds {@code changed}/{@code previousHash}/
   * {@code fetchedBytes}/{@code fetchedFromUrl} alongside {@code settings}
   * so the UI can tell the operator exactly what happened — "hash
   * updated to X" vs "hash unchanged (deployed file already matches)".
   * Falls back to the legacy shape (response.data.data is the
   * settings row directly) when the backend hasn't been redeployed yet.
   *
   * @returns {Promise<{settings: object, changed?: boolean, previousHash?: string, fetchedBytes?: number, fetchedFromUrl?: string}>}
   */
  async recomputeSri() {
    const response = await apiClient.post('/api/v1.0/rum/platform-settings/recompute-sri');
    const payload = response.data?.data || {};
    // New shape: { settings, changed, previousHash, ... }
    if (payload && payload.settings) {
      this._platformSettings = payload.settings;
      return {
        settings: payload.settings,
        changed: payload.changed,
        previousHash: payload.previousHash,
        fetchedBytes: payload.fetchedBytes,
        fetchedFromUrl: payload.fetchedFromUrl,
        message: response.data?.message,
      };
    }
    // Legacy shape: payload IS the settings row.
    this._platformSettings = payload;
    return { settings: payload, message: response.data?.message };
  }

  /**
   * Download the JavaScript RUM SDK so a customer can self-host it.
   *
   * Fetches the REAL bundle from the operator-configured CDN URL (the same
   * file the integration snippet loads, so its SRI hash matches). If the
   * fetch is blocked (e.g. the operator points the CDN at another origin
   * without CORS), the URL is opened in a new tab so the browser can save it.
   *
   * Note: VerOps supports only this JavaScript (browser) RUM SDK. The Java
   * RUM SDK that older versions advertised here (downloadJavaSDK / Maven /
   * Gradle snippets for io.verops:rum-java-sdk) was a leftover from an old
   * version, was never published, and was removed (#138).
   *
   * @returns {Promise<void>}
   */
  async downloadSDK() {
    const url = this.getSDKCdnUrl();
    let response;
    try {
      response = await fetch(url, { credentials: 'omit' });
    } catch (error) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    if (!response.ok) {
      throw new Error(`SDK download failed: HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = url.split('/').pop().split('?')[0] || 'verops-rum.min.js';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  }
}

// Create and export a singleton instance
const appManagementApi = new AppManagementApi();

export default appManagementApi;

// Export the class for creating custom instances
export { AppManagementApi };
